import {
  Collapsible,
  InputLabel,
  ModelForm,
  ModelInput,
  ModelSelect,
  SelectInputField,
  SelectInputIcon,
  TextFieldInput,
  useStyledTheme,
} from '@equifinance/shared';
import styled from 'styled-components';
import {
  CreditItem, PaymentShouldBePaidToType, UnsecuredAccountType, CreditTypes, ConsolidationType,
  useCreditContext,
} from '../../../../contexts/credit';
import { CreditDetailsContainer, CreditDetailsRow } from '../../CreditDetails/CreditDetails';

const ConsolidationDetailsRow = styled(CreditDetailsRow)` ${({ theme }) => `
  gap: ${theme.padding[4]};
  align-items: flex-start;

  ${SelectInputField}[disabled],
  ${TextFieldInput}:disabled {
    background-color: transparent;
    color: ${theme.colours.neutral[6]};
    border: 1px solid ${theme.colours.neutral[6]};
  }

  ${SelectInputIcon} {
    color: ${theme.colours.neutral[4]};
  }
  
  ${InputLabel}[disabled] {
    color: ${theme.colours.neutral[6]};
  }
`}`;

export default function ConsolidationDetails({
  data,
  type = 'secured',
}: {
  data: CreditItem,
  type?: 'secured' | 'unsecured',
}) {
  const { updateCreditItem, credit } = useCreditContext();
  const { errors, warnings } = credit;
  const theme = useStyledTheme();

  const update = (values: any, disableIncrementUpdateCount: boolean = false) => updateCreditItem(
    values,
    data.id,
    type === 'secured' ? CreditTypes.SECURED : CreditTypes.UNSECURED,
    disableIncrementUpdateCount,
  );

  const PaidToOptions = () => {
    if (type === 'secured') {
      return {
        Lender: PaymentShouldBePaidToType.Lender,
        ThirdParty: PaymentShouldBePaidToType.ThirdParty,
      };
    }

    return ([
      UnsecuredAccountType.Communication,
      UnsecuredAccountType.FamilyMember,
    ]).includes(UnsecuredAccountType[data.accountType as keyof typeof UnsecuredAccountType])
      ? Object.entries(PaymentShouldBePaidToType).reduce((prev, [key, value]) => ({
        ...prev,
        ...(key !== PaymentShouldBePaidToType.Lender ? { [key]: value } : {}),
      }), {})
      : PaymentShouldBePaidToType;
  };

  if (!data.consolidationType || ConsolidationType[data.consolidationType] === ConsolidationType.NoConsolidation) {
    return null;
  }

  return (
    <ModelForm model={data} update={update} errors={errors} warnings={warnings}>
      <Collapsible
        id={`${credit.id}-consolidation-details`}
        title="Consolidation details"
        level={2}
      >
        <CreditDetailsContainer>
          <ConsolidationDetailsRow>
            <ModelSelect
              field="consolidationPayeeType"
              label="Payment to be made to"
              options={PaidToOptions()}
              disabled={data.accountType === 'FirstCharge'}
              containerCss="max-width: 20%;"
              required
            />
            {![
              PaymentShouldBePaidToType.Applicants,
              PaymentShouldBePaidToType.CreditorViaApplicant,
            ].includes(PaymentShouldBePaidToType[data.consolidationPayeeType])
              && (
                <>
                  <ModelInput
                    field="consolidationPayeeName"
                    label="Name of creditor"
                    disabled={data.consolidationPayeeType === 'Lender'}
                    required
                  />
                  <ModelInput
                    field="consolidationAccountNumber"
                    label="Account no."
                    containerCss={`
                      max-width: ${theme.padding[12]};
                    `}
                    required
                    mutateValue={(v) => v?.replace(/\D/g, '')?.slice(0, 8) ?? ''}
                  />
                  <ModelInput
                    field="consolidationSortCode"
                    label="Sort code"
                    containerCss={`
                      max-width: ${theme.padding[11]};
                    `}
                    required
                    mutateValue={(v) => v?.replace(/\D/g, '')?.slice(0, 6) ?? ''}
                  />
                  <ModelInput
                    field="consolidationReferenceNumber"
                    label="Reference"
                    required
                  />
                </>
              )}
          </ConsolidationDetailsRow>
        </CreditDetailsContainer>
      </Collapsible>
    </ModelForm>
  );
}
